import clsx from 'clsx'
import renderInputField from 'components/FormControls/textInput'
import PropTypes from 'prop-types'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import * as Yup from 'yup'
import LoginField from '../subcomponents/LoginField'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import SubmitButtons from 'modules/Forms/Account/Create/subcomponents/submitButtons'
import advertiserInfoValidationRulesExtended from 'modules/Forms/Account/Create/subcomponents/advertiser-info-validation-rules-extended'
import initialAdvertiserInfo from 'modules/Forms/Account/Create/initialAdvertiserInfo'
import { useSelector } from 'react-redux'
import omit from 'lodash/omit'
import AdvertiserInfoGroup from 'modules/Forms/Account/Create/subcomponents/AdvertiserInfoGroup'

export const VkAdBloggerSchema = (networkType) =>
  Yup.object()
    .shape({
      logins: Yup.array().of(Yup.object()),
      login: Yup.string().when('logins', ([logins], schema) =>
        logins
          ? schema.strip()
          : schema
              .test('baseLoginTest', '', baseLoginValidator)
              .required('common::validation_messages.required'),
      ),
      network: Yup.string().required('common::validation_messages.required'),
      vk_user_id: Yup.string().required('common::validation_messages.required'),
      manager_full_name: Yup.string().required('common::validation_messages.required'),
    })
    .concat(advertiserInfoValidationRulesExtended(networkType))

let CreateVkAdBloggerAccountForm = ({
  handleSubmit,
  submitting,
  onClose,
  autofill,
  networkType,
  formHeader,
  networkId,
  form,
  change,
  array,
  multiLogins,
  initialValues,
}) => {
  const { t } = useTranslation()
  const submitAccountData = useAccountCreateFormSubmit(form, onClose, multiLogins)
  const selector = formValueSelector(form)
  const { advertiser_info } = useSelector((state) => selector(state, 'email', 'advertiser_info'))

  const submit = useCallback(
    (values) => submitAccountData(omit(values, ['advertiser_template'])),
    [submitAccountData],
  )

  return (
    <>
      {formHeader}

      <form
        onSubmit={handleSubmit(submit)}
        noValidate
      >
        <Field
          disabled
          name="network"
          hidden
          fullWidth
          component={renderInputField}
          t={t}
        />

        <div className={'margin-bottom-16'}>
          <LoginField
            form={form}
            multiLogins={multiLogins}
            array={array}
            autofill={autofill}
            networkId={networkId}
            label={'services::forms.register_account.vk-adblogger.fields.login.label'}
          />
        </div>

        <div className={clsx('margin-bottom-16')}>
          <Field
            name="vk_user_id"
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.vk-adblogger.fields.vkUserId.label')}
            t={t}
          />
        </div>

        <div className={'margin-bottom-16'}>
          <Field
            name="manager_full_name"
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.vk-adblogger.fields.managerFullName.label')}
            t={t}
          />
        </div>

        <AdvertiserInfoGroup
          form={form}
          change={change}
          advertiser_info={advertiser_info}
          networkType={networkType}
          initialValues={initialValues}
        />

        <SubmitButtons
          submitted={submitAccountData.submitted}
          submitting={submitting}
          onClose={onClose}
        />
      </form>
    </>
  )
}

CreateVkAdBloggerAccountForm = reduxForm({
  form: 'CreateVkAdBloggerAccountForm',
  initialValues: {
    login: '',
    network: '',
    vk_user_id: '',
    manager_full_name: '',
    advertiser_info: { ...initialAdvertiserInfo },
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateVkAdBloggerAccountForm)

CreateVkAdBloggerAccountForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  autofill: PropTypes.func,
  networkId: PropTypes.string,
  networkType: PropTypes.string,
  initialValues: PropTypes.object,
  form: PropTypes.string,
  change: PropTypes.func,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
  formHeader: PropTypes.node,
}

export default CreateVkAdBloggerAccountForm
